import { useCallback } from 'react';
import {
  ScratchpadRecommendation,
  ScratchpadRecommendationRequestType,
} from '../../../../../../../api/graphql/generated';
import { useGetUnitOfMeasureSystem } from '../../../../../../../api/hooks/user-preference.hooks';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { logger } from '../../../../../../../util/logger';
import { WorkPiecePositionName } from '../../../../../../../util/part.util';
import { ParametersRecommendationInfo } from '../../../../../shared/component/flow-item-parameter/parameter-list';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import {
  FlowItemForm,
  FlowItemParameter,
  SystemUnit,
} from '../../../../../shared/model/flow.types';
import { FlowItemService } from '../../../../../shared/service/service.types';
import { convertParameterValueToUOM } from '../../../../../shared/util/flow-parameter.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { useCreateRecommendationForForm } from './recommendation.hook';
import { getPositionalSettingsRecommendation } from './settings-recommendation.utils';

// Parameters that should be excluded from recommendation responses
// These are typically input parameters or problematic enum values
const PARAMETERS_TO_EXCLUDE_FROM_RECOMMENDATIONS: BeaconWeldingData.FlowItemParameterRef[] =
  [
    BeaconWeldingData.FlowItemParameterRef.WELD_TYPE,
    BeaconWeldingData.FlowItemParameterRef.WEAVE_ENABLED,
    // Add more parameters here as needed
  ];

type WeldParameterRecommendation = FlowItemParameter & {
  minValue: number;
  maxValue: number;
};

export type WeldSettingsRecommendation = {
  parameters: WeldParameterRecommendation[];
  weldPosition: WorkPiecePositionName;
};

export type SingleWeldRecommendation = Omit<
  ScratchpadRecommendation,
  'outputs'
> & {
  recommendation: WeldSettingsRecommendation;
};

export type MultipleWeldRecommendations = Omit<
  ScratchpadRecommendation,
  'outputs'
> & {
  recommendations: WeldSettingsRecommendation[];
};

type CreateWeldRecommendationForFormType = (
  form: FlowItemForm,
  tags: Record<string, unknown>
) => Promise<SingleWeldRecommendation | undefined>;

const useCreateSettingsRecommendationForForm =
  (): CreateWeldRecommendationForFormType => {
    const createRecommendation = useCreateRecommendationForForm({
      appSlug: BeaconWeldingData.APP_SLUG,
      recommendationType: ScratchpadRecommendationRequestType.SETTINGS,
    });

    const system = useGetUnitOfMeasureSystem();

    const { flowItemService, flow, flowService } =
      useFlowServices<BeaconWeldingData.FlowConfig>();

    const getSettingsRecommendationForForm = useCallback(
      async (
        form: FlowItemForm,
        tags: Record<string, unknown>
      ): Promise<SingleWeldRecommendation | undefined> => {
        let flowItem = flowItemService.convertFormToFlowItem(form, system);

        // remove the params to be predicted
        flowItem = flowItemService.setParameterValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED,
          undefined
        );

        const { config } =
          flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
            flow,
            tags
          );

        const recommendation = await createRecommendation(flowItem, config);

        if (!recommendation) {
          return;
        }

        const horizontalRecommendation = getPositionalSettingsRecommendation(
          recommendation,
          WorkPiecePositionName.HORIZONTAL
        );

        if (horizontalRecommendation === undefined) {
          // if this happens, the UI will need to change to accomodate a changing API
          logger.error(
            'no horizontal position recommendation found in settings recommendation',
            {
              recommendations: recommendation.outputs,
            }
          );
          return;
        }

        const parameters = convertWeldParameterValuesToUOM(
          flowItemService,
          horizontalRecommendation.parameters,
          system
        );

        const { outputs: _outputs, ...rest } = recommendation;

        return {
          ...rest,
          recommendation: {
            ...horizontalRecommendation,
            parameters,
          },
        };
      },
      [flowItemService, system, flowService, flow, createRecommendation]
    );

    return getSettingsRecommendationForForm;
  };

type CreateMultipleWeldRecommendationsForFormType = (
  form: FlowItemForm,
  tags: Record<string, unknown>
) => Promise<MultipleWeldRecommendations | undefined>;

const useCreateMultipleSettingsRecommendationsForForm =
  (): CreateMultipleWeldRecommendationsForFormType => {
    const createRecommendation = useCreateRecommendationForForm({
      appSlug: BeaconWeldingData.APP_SLUG,
      recommendationType: ScratchpadRecommendationRequestType.SETTINGS,
    });

    const system = useGetUnitOfMeasureSystem();

    const { flowItemService, flow, flowService } =
      useFlowServices<BeaconWeldingData.FlowConfig>();

    const getMultipleSettingsRecommendationsForForm = useCallback(
      async (
        form: FlowItemForm,
        tags: Record<string, unknown>
      ): Promise<MultipleWeldRecommendations | undefined> => {
        let flowItem = flowItemService.convertFormToFlowItem(form, system);

        // remove the params to be predicted
        flowItem = flowItemService.setParameterValue(
          flowItem,
          BeaconWeldingData.FlowItemParameterRef.TRAVEL_SPEED,
          undefined
        );

        const { config } =
          flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
            flow,
            tags
          );

        const recommendation = await createRecommendation(flowItem, config);

        if (!recommendation) {
          return;
        }

        const allRecommendations: WeldSettingsRecommendation[] =
          recommendation.outputs.similar_settings || [];

        if (allRecommendations.length === 0) {
          logger.error('no recommendations found in settings recommendation', {
            recommendations: recommendation.outputs,
          });
          return;
        }

        // Filter out excluded parameters from each recommendation
        const filteredRecommendations = allRecommendations.map((rec) => ({
          ...rec,
          parameters: rec.parameters.filter(
            (param) =>
              !PARAMETERS_TO_EXCLUDE_FROM_RECOMMENDATIONS.includes(
                param.ref as BeaconWeldingData.FlowItemParameterRef
              )
          ),
        }));

        // Convert all recommendations to user's UOM
        const convertedRecommendations = filteredRecommendations.map((rec) => {
          const parameters = convertWeldParameterValuesToUOM(
            flowItemService,
            rec.parameters,
            system
          );

          return {
            ...rec,
            parameters,
          };
        });

        const { outputs: _outputs, ...rest } = recommendation;

        return {
          ...rest,
          recommendations: convertedRecommendations,
        };
      },
      [flowItemService, system, flowService, flow, createRecommendation]
    );

    return getMultipleSettingsRecommendationsForForm;
  };

const setFormParamValues = async (
  parameters: FlowItemParameter[],
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => void,
  formikValuePath: string
) => {
  // set recommended values and labels
  for (const param of parameters ?? []) {
    const value = param.value;
    const fieldPath = `${formikValuePath}.parameters.${param.ref}.value`;

    setFieldValue(fieldPath, value);
  }
};

const createParamSubtitlesForSettingsRecommendations = (
  recommendation: WeldSettingsRecommendation
): ParametersRecommendationInfo => {
  const recommendationSubtitles: ParametersRecommendationInfo = {};

  // set recommended values and labels
  for (const param of recommendation?.parameters ?? []) {
    const min = param.minValue;
    const max = param.maxValue;
    const recommendedText =
      getLocalizedText('common:recommended') ?? 'Recommended';

    recommendationSubtitles[param.ref] = {
      subtext: `${recommendedText}: ${min} - ${max}`,
    };
  }

  return recommendationSubtitles;
};

const convertWeldParameterValuesToUOM = (
  flowItemService: FlowItemService<BeaconWeldingData.FlowConfig>,
  parameters: WeldParameterRecommendation[],
  system: SystemUnit
): WeldParameterRecommendation[] => {
  return parameters
    .filter((param) => {
      // Exclude empty recommendations, such as when the api
      // sometimes can predict a param (e.g. travel speed) but for
      // this instance it didn't calculate a value.
      // This can happen when the inputs don't meet criteria.
      // This check avoids the UI trying to update form fields with nothing.
      return param.ref !== undefined && param.value !== undefined;
    })
    .map((param) => {
      const metadata = flowItemService.getParameterMetadata(
        BeaconWeldingData.FlowItemRef.WELD,
        param.ref
      );

      if (metadata && metadata.value.uom && metadata.value.type === 'number') {
        try {
          const value = convertParameterValueToUOM(
            metadata.value,
            system,
            param.value,
            false
          );

          const minValue =
            param.minValue != null
              ? convertParameterValueToUOM(
                  metadata.value,
                  system,
                  param.minValue,
                  false
                )
              : param.minValue;

          const maxValue =
            param.maxValue != null
              ? convertParameterValueToUOM(
                  metadata.value,
                  system,
                  param.maxValue,
                  false
                )
              : param.maxValue;

          return {
            ...param,
            value,
            minValue,
            maxValue,
          };
        } catch (error) {
          logger.error('UOM conversion failed for parameter', {
            paramRef: param.ref,
            paramValue: param.value,
            paramMinValue: param.minValue,
            paramMaxValue: param.maxValue,
            valueType: typeof param.value,
            minValueType: typeof param.minValue,
            maxValueType: typeof param.maxValue,
            metadataType: metadata.value.type,
            metadataUom: metadata.value.uom,
            error: error instanceof Error ? error.message : String(error),
          });
          return param;
        }
      }

      return param;
    });
};

export {
  createParamSubtitlesForSettingsRecommendations,
  setFormParamValues,
  useCreateSettingsRecommendationForForm,
  useCreateMultipleSettingsRecommendationsForForm,
};
